import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import GlobalSearch from "@/components/GlobalSearch";
import QuickNoteButton from "@/components/QuickNoteButton";
import {
    LayoutDashboard,
    Users,
    Contact,
    FolderKanban,
    BarChart3,
    Settings,
    LogOut,
    Bell,
    BookMarked,
    Trophy,
    Building2,
    ScrollText,
    History,
    ListChecks,
} from "lucide-react";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/clienti", label: "Clienti", icon: Users, testId: "nav-clienti" },
    { to: "/collaboratori", label: "Collaboratori", icon: Contact, testId: "nav-collaboratori" },
    { to: "/progetti", label: "Progetti", icon: FolderKanban, testId: "nav-progetti" },
    { to: "/progetti-interni", label: "Progetti interni", icon: Building2, testId: "nav-progetti-interni" },
    { to: "/piani-giornalieri", label: "Piani giornalieri", icon: ListChecks, testId: "nav-daily-plans" },
    { to: "/successi", label: "Successi", icon: Trophy, testId: "nav-successi" },
    { to: "/produzione", label: "Report", icon: BarChart3, testId: "nav-produzione" },
    { to: "/sop", label: "SOP", icon: BookMarked, testId: "nav-sop" },
    { to: "/policy", label: "Policy", icon: ScrollText, testId: "nav-policy" },
    { to: "/time-machine", label: "Time Machine", icon: History, testId: "nav-time-machine" },
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen flex bg-neutral-50">
            {/* Sidebar (white) — sticky so it fills the viewport height */}
            <aside className="w-56 bg-white text-neutral-800 flex flex-col border-r border-neutral-200 sticky top-0 h-screen" data-testid="app-sidebar">
                <div className="h-14 px-5 border-b border-neutral-200 flex items-center">
                    <div className="font-display text-2xl font-semibold tracking-tight text-neutral-900 lowercase">jester</div>
                </div>
                <nav className="flex-1 py-3">
                    {NAV.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.to === "/"}
                            data-testid={it.testId}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-5 py-2 text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-neutral-100 text-neutral-900 border-l-2 border-[#FFE500]"
                                        : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 border-l-2 border-transparent"
                                }`
                            }
                        >
                            <it.icon size={16} />
                            {it.label}
                        </NavLink>
                    ))}
                    {user?.role === "admin" && (
                        <NavLink
                            to="/impostazioni"
                            data-testid="nav-impostazioni"
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-5 py-2 text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-neutral-100 text-neutral-900 border-l-2 border-[#FFE500]"
                                        : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 border-l-2 border-transparent"
                                }`
                            }
                        >
                            <Settings size={16} />
                            Impostazioni
                        </NavLink>
                    )}
                </nav>
                <div className="px-4 py-3 border-t border-neutral-200">
                    <button
                        onClick={async () => {
                            await logout();
                            navigate("/login");
                        }}
                        className="flex items-center gap-2 text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
                        data-testid="logout-btn"
                    >
                        <LogOut size={14} /> Esci
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="h-14 bg-white border-b border-neutral-200 flex items-center px-6 gap-4">
                    <GlobalSearch />
                    <div className="ml-auto flex items-center gap-2.5">
                        <div className="text-right">
                            <div className="text-xs font-semibold text-neutral-800 leading-tight">
                                {user?.first_name} {user?.last_name}
                            </div>
                            <div className="text-[11px] text-neutral-500 leading-tight">
                                {user?.role === "admin" ? "Amministratore" : "Collaboratore"}
                            </div>
                        </div>
                        <button onClick={() => navigate("/profilo")} data-testid="profile-btn">
                            <UserAvatar user={user} size={30} />
                        </button>
                    </div>
                </header>

                <main className="flex-1 overflow-auto">
                    <Outlet />
                </main>
                <QuickNoteButton />
            </div>
        </div>
    );
}
